import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { format, parse, startOfWeek, getDay, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  Calendar as BigCalendar,
  dateFnsLocalizer,
  Views,
} from "react-big-calendar";
import { fetchQuickQuotes } from "../redux/features/quickQuateSlice";
import "react-big-calendar/lib/css/react-big-calendar.css";
import enUS from "date-fns/locale/en-US";

// Colors for service types
const SERVICE_COLORS = {
  commercial_cleaning: "#4F46E5",
  residential_cleaning: "#10B981",
  industrial_cleaning: "#F59E0B",
  specialized_cleaning: "#EF4444",
};

// Helper function to format service type names
const formatServiceType = (type) => {
  if (!type) return "";
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function CalendarView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { quotes = [] } = useSelector((state) => state.quickQuote);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    dispatch(fetchQuickQuotes());
  }, [dispatch]);

  // Format quotes for calendar events
  const events = useMemo(() => {
    return quotes.map((quote) => {
      const eventDate = new Date();
      return {
        id: quote.id,
        title: `${quote.name} - ${formatServiceType(quote.serviceType)}`,
        start: eventDate,
        end: eventDate,
        quote: quote,
        color: SERVICE_COLORS[quote.serviceType],
      };
    });
  }, [quotes]);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: "5px",
      opacity: 0.8,
      color: "white",
      border: "none",
      display: "block",
    },
  });

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  return (
    <div className="p-6 space-y-6">
      {/* <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Quote Calendar</h1>
      </div> */}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Quote Calendar</h1>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← Back
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div style={{ height: "700px" }}>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            views={["month", "week", "day"]}
            step={60}
            showMultiDayTimes
            defaultDate={new Date()}
            date={date}
            onNavigate={handleNavigate}
            view={view}
            onView={handleViewChange}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => setSelectedEvent(event.quote)}
            popup
            selectable
            className="text-sm"
          />
        </div>
      </div>

      {/* Quote Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Quote Details
                </h2>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Customer Name
                  </label>
                  <p className="mt-1 text-lg font-medium text-gray-900">
                    {selectedEvent.name}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="mt-1 text-lg text-gray-900">
                    {selectedEvent.email}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Phone
                  </label>
                  <p className="mt-1 text-lg text-gray-900">
                    {selectedEvent.mobileNumber}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Service Type
                  </label>
                  <span
                    className="mt-1 px-3 py-1 inline-flex text-sm font-semibold rounded-full"
                    style={{
                      backgroundColor: `${
                        SERVICE_COLORS[selectedEvent.serviceType]
                      }20`,
                      color: SERVICE_COLORS[selectedEvent.serviceType],
                    }}
                  >
                    {formatServiceType(selectedEvent.serviceType)}
                  </span>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Description
                  </label>
                  <p className="mt-1 text-lg text-gray-900">
                    {selectedEvent.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
